import { useCallback, useEffect, useMemo, useState } from 'react';
import type {
  AdjustmentSuggestionView,
  CelebrationCue,
  HabitWeekReport,
  ObjectiveCheckpointAnswer,
  ObjectiveDateSuggestionView,
  ObjectiveId,
  ObjectiveReviewItem,
  RewardConfig,
  WeekStart,
  WeeklyReviewView,
} from '@contracts';
import { DEFAULT_REWARD_CONFIG } from '@contracts';
import { useDomainApi, useDomainVersion } from '@ui/shared/domain/DomainApiContext';
import { SkeletonBlock } from '@ui/shared/primitives/Skeleton';
import { resolveLevelIdentity } from '@ui/shared/rewards/levelProgress';
import { XPToast } from '@ui/shared/celebrations/XPToast';
import { LevelUpMoment } from '@ui/shared/celebrations/LevelUpMoment';
import { BadgeEarnSheet } from '@ui/shared/celebrations/BadgeEarnSheet';
import { useCelebrationQueue } from '@ui/shared/celebrations/useCelebrationQueue';
import { formatWeekRange } from '@ui/shared/format/date';
import { VerdictPanel } from './VerdictPanel';
import { HabitsTable } from './HabitsTable';
import { SuggestionPanel } from './SuggestionPanel';
import { ObjectivesPanel } from './ObjectivesPanel';
import { FinishBar } from './FinishBar';

/**
 * Weekly review screen (desktop-weekly-review.md v3.0, widened per the
 * Today/Habits/Objectives desktop-consistency pass). Originally a
 * shell-less, single scrolling column of stacked elevation.1 panels capped
 * at 1040px (WR-21/§2.2, D1) — now widened to the same 1600px content
 * width as Dashboard/Objective Form/Today, restructured into that same
 * two-column shape (main content + a sticky `ThisWeekSummary` sidebar)
 * rather than just stretching the single column wider, since a habits
 * table/objective cards still read better at a controlled line length —
 * the sidebar is what gives the extra width a reason to exist, mirroring
 * the Realism Engine sidebar's own role on the Objective Form screen.
 * `DesktopShell` already provides the rail/back-navigation chrome (the
 * "back-to-Today" comment below refers to the sub-1024px `BackToTodayShell`
 * fallback only).
 *
 * Component boundary (AC-W24): no component in this render tree is shared
 * with TodayView.tsx's phone-first layout — shared pieces are limited to
 * cross-surface primitives already used by RewardsScreen.tsx (SkeletonBlock,
 * celebration components, icon set), matching that AC's exact requirement.
 * `ThisWeekSummary` is purely presentational over values the shell already
 * computes (`pendingCount`, `rewardConfig.xp.reviewCompleted`, etc.) — no
 * new domain math added.
 */
export function WeeklyReviewScreenDesktop({
  onBack,
  onEditObjective,
}: {
  readonly onBack: () => void;
  /** WR-12: "Re-scope it" deep-links to the existing EditObjectiveScreen (Phase 1, unchanged). */
  readonly onEditObjective: (objectiveId: ObjectiveId) => void;
}) {
  const api = useDomainApi();
  const version = useDomainVersion();

  const [weekStart, setWeekStart] = useState<WeekStart | null>(null);
  const [review, setReview] = useState<WeeklyReviewView | null>(null);
  const [rewardConfig, setRewardConfig] = useState<RewardConfig>(DEFAULT_REWARD_CONFIG);
  const [loadError, setLoadError] = useState(false);

  // WR-10/AC-W12: decline is explicitly NOT a domain event (no XP, no log
  // entry — DomainApi has no declineSuggestion command). Suppressing a
  // declined suggestion for "the rest of this review session" is therefore a
  // UI-local concern: a set of `habitId|reviewWeek` keys held in component
  // state, cleared whenever the review week changes.
  const [declined, setDeclined] = useState<ReadonlySet<string>>(new Set());

  const celebration = useCelebrationQueue({
    resolveIdentity: (level) =>
      resolveLevelIdentity(
        { xpTotal: 0, level, xpIntoCurrentLevel: 0, xpRequiredForNextLevel: 100, streakDays: 0 },
        rewardConfig,
      ).identity,
  });

  const handleCue = useCallback(
    (cue: CelebrationCue, sourceLabel: string) => {
      if (cue.xpAwarded <= 0 && !cue.levelUp && cue.badgesEarned.length === 0) return;
      void api.getRewardSnapshot().then((snapshot) => {
        celebration.enqueue(cue, sourceLabel, snapshot.xpTotal);
      });
    },
    [api, celebration],
  );

  const load = useCallback(async () => {
    try {
      const [currentWeek, config] = await Promise.all([api.getCurrentReviewWeek(), api.getRewardConfig()]);
      const view = await api.getWeeklyReview(currentWeek);
      setWeekStart(currentWeek);
      setReview(view);
      setRewardConfig(config);
      setLoadError(false);
    } catch {
      setLoadError(true);
    }
  }, [api]);

  useEffect(() => {
    void load();
  }, [load, version]);

  useEffect(() => {
    setDeclined(new Set());
  }, [weekStart]);

  const declineSuggestion = useCallback((habitId: string, reviewWeek: WeekStart) => {
    setDeclined((prev) => new Set(prev).add(`${habitId}|${reviewWeek}`));
  }, []);

  const visibleHabits: readonly HabitWeekReport[] = useMemo(() => {
    if (!review) return [];
    return review.habits.map((h) =>
      h.suggestion && declined.has(`${h.habitId}|${h.suggestion.reviewWeek}`)
        ? { ...h, suggestion: null }
        : h,
    );
  }, [review, declined]);

  const suggestingHabits = useMemo(
    () => visibleHabits.filter((h): h is HabitWeekReport & { suggestion: AdjustmentSuggestionView } => h.suggestion !== null),
    [visibleHabits],
  );

  const untouchedObjectives = useMemo(
    () => (review ? review.objectives.filter((o) => !o.touchedThisWeek && o.checkpointAnswer === null) : []),
    [review],
  );

  const pendingCount = suggestingHabits.length + untouchedObjectives.length;

  const acceptHabitSuggestion = useCallback(
    async (suggestion: AdjustmentSuggestionView) => {
      const cue = await api.acceptSuggestion(suggestion);
      handleCue(cue, 'Smart adjustment');
      await load();
    },
    [api, handleCue, load],
  );

  const acceptObjectiveDate = useCallback(
    async (suggestion: ObjectiveDateSuggestionView) => {
      const cue = await api.acceptObjectiveDateSuggestion(suggestion);
      handleCue(cue, 'Date adjusted');
      await load();
    },
    [api, handleCue, load],
  );

  const answerCheckpoint = useCallback(
    async (objectiveId: ObjectiveId, answer: ObjectiveCheckpointAnswer) => {
      if (!weekStart) return;
      await api.answerObjectiveCheckpoint(objectiveId, weekStart, answer);
      await load();
    },
    [api, weekStart, load],
  );

  const finishReview = useCallback(async () => {
    if (!weekStart) return;
    const cue = await api.completeReview(weekStart);
    // §1.6a: coalesced toast — "+55 XP · Review + 1 target met" — derived
    // from config math (reviewCompleted + N * weeklyTargetBonus) rather than
    // a second server-side count, since the cue only carries the total.
    const bonusUnit = rewardConfig.xp.weeklyTargetBonus;
    const reviewXp = rewardConfig.xp.reviewCompleted;
    const targetsMet =
      bonusUnit > 0 ? Math.max(0, Math.round((cue.xpAwarded - reviewXp) / bonusUnit)) : 0;
    const sourceLabel =
      targetsMet > 0
        ? `Review + ${targetsMet} target${targetsMet === 1 ? '' : 's'} met`
        : 'Weekly review';
    handleCue(cue, sourceLabel);
    await load();
  }, [api, weekStart, rewardConfig, handleCue, load]);

  if (loadError) {
    return (
      <main aria-label="Weekly review" style={{ padding: 'var(--space-8) var(--layout-page-margin-desktop)', maxWidth: '1600px', margin: '0 auto' }}>
        <p style={{ fontSize: 'var(--type-body-size)', color: 'var(--text-secondary)' }}>
          Couldn't load the week. Your data is safe — try again.
        </p>
        <button
          type="button"
          onClick={() => void load()}
          style={{
            marginTop: 'var(--space-4)',
            minHeight: 'var(--touch-target-min)',
            padding: '0 var(--space-5)',
            borderRadius: 'var(--radius-md)',
            border: '1px solid var(--border-strong)',
            color: 'var(--text-primary)',
            fontWeight: 600,
            fontSize: 'var(--type-body-size)',
          }}
        >
          Retry
        </button>
      </main>
    );
  }

  if (!review || !weekStart) {
    return (
      <main aria-label="Weekly review" style={{ padding: 'var(--space-8) var(--layout-page-margin-desktop)', maxWidth: '1600px', margin: '0 auto' }}>
        <SkeletonBlock height={32} width={220} />
        <div style={{ height: 'var(--space-6)' }} />
        <SkeletonBlock height={160} />
        <div style={{ height: 'var(--space-5)' }} />
        <SkeletonBlock height={120} />
        <div style={{ height: 'var(--space-5)' }} />
        <SkeletonBlock height={120} />
      </main>
    );
  }

  const isEmpty = review.habits.length === 0 && review.objectives.length === 0;

  return (
    <main
      aria-label="Weekly review"
      style={{
        padding: 'var(--space-8) var(--layout-page-margin-desktop) var(--space-16)',
        maxWidth: '1600px',
        margin: '0 auto',
      }}
    >
      <ReviewHeader
        weekStart={weekStart}
        available={review.available}
        completed={review.completed}
        reviewCompletedXp={rewardConfig.xp.reviewCompleted}
      />

      {isEmpty ? (
        <EmptyReview onBack={onBack} />
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 380px', gap: 'var(--space-6)', alignItems: 'flex-start', marginTop: 'var(--space-6)' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--layout-desktop-panel-gap)', minWidth: 0 }}>
            {!review.available && (
              <p style={{ margin: 0, fontSize: 'var(--type-body-size)', color: 'var(--text-secondary)' }}>
                The review for this week opens Sunday.
              </p>
            )}

            <VerdictPanel review={review} habits={visibleHabits} completed={review.completed} />

            {review.habits.length > 0 && <HabitsTable habits={visibleHabits} />}

            {review.available && suggestingHabits.length > 0 && (
              <SuggestionPanel
                suggestions={suggestingHabits}
                adjustmentAcceptedXp={rewardConfig.xp.adjustmentAccepted}
                onAccept={acceptHabitSuggestion}
                onDecline={(habitId, reviewWeek) => declineSuggestion(habitId, reviewWeek)}
              />
            )}

            {review.available && review.habits.length > 0 && suggestingHabits.length === 0 && !hasAnyTrendHistory(review) && (
              <p style={{ margin: 0, fontSize: 'var(--type-caption-size)', color: 'var(--text-secondary)' }}>
                Adjustment suggestions appear once there's two weeks of history.
              </p>
            )}

            {review.objectives.length > 0 && (
              <ObjectivesPanel
                objectives={review.objectives}
                dateAdjustmentAcceptedXp={rewardConfig.xp.dateAdjustmentAccepted}
                onAcceptDateSuggestion={acceptObjectiveDate}
                onAnswerCheckpoint={answerCheckpoint}
                onEditObjective={onEditObjective}
              />
            )}

            {review.available && (
              <FinishBar
                completed={review.completed}
                pendingCount={pendingCount}
                reviewCompletedXp={rewardConfig.xp.reviewCompleted}
                onFinish={finishReview}
              />
            )}
          </div>

          <ThisWeekSummary
            weekStart={weekStart}
            available={review.available}
            completed={review.completed}
            pendingCount={pendingCount}
            reviewCompletedXp={rewardConfig.xp.reviewCompleted}
            habitCount={review.habits.length}
            objectiveCount={review.objectives.length}
          />
        </div>
      )}

      {celebration.xpToast && (
        <XPToast amount={celebration.xpToast.amount} sourceLabel={celebration.xpToast.sourceLabel} onDismiss={celebration.dismissToast} />
      )}
      {celebration.levelUp && (
        <LevelUpMoment
          level={celebration.levelUp.level}
          identity={celebration.levelUp.identity}
          xpTotal={celebration.levelUp.xpTotal}
          onDismiss={celebration.dismissLevelUp}
        />
      )}
      {celebration.visibleBadgeId && (
        <ReviewBadgeEarnLoader badgeId={celebration.visibleBadgeId} onDismiss={celebration.dismissNextBadge} />
      )}
    </main>
  );
}

/** No `HabitWeekReport.trend` has any entries anywhere — a true first-review state (§2 "First week"). */
function hasAnyTrendHistory(review: WeeklyReviewView): boolean {
  return review.habits.some((h) => h.trend.length > 0);
}

function ReviewHeader({
  weekStart,
  available,
  completed,
  reviewCompletedXp,
}: {
  readonly weekStart: WeekStart;
  readonly available: boolean;
  readonly completed: boolean;
  readonly reviewCompletedXp: number;
}) {
  return (
    // §1.1: the back-to-Today link is App.tsx's wrapper (the SAME wrapper
    // pattern RewardsScreen.tsx is already given) — not duplicated here,
    // per the spec's explicit "reuse that exact wrapper pattern rather than
    // inventing a second one" instruction.
    <header>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', flexWrap: 'wrap', gap: 'var(--space-3)' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--space-3)' }}>
          <h1
            style={{
              margin: 0,
              fontSize: 'var(--type-display-size)',
              fontWeight: 'var(--type-display-weight)',
              color: 'var(--text-primary)',
            }}
          >
            Weekly review
          </h1>
          <span style={{ fontSize: 'var(--type-heading-size)', fontWeight: 'var(--type-heading-weight)', color: 'var(--text-secondary)' }}>
            {formatWeekRange(weekStart)}
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
          <span
            style={{
              padding: '0 var(--space-3)',
              minHeight: '28px',
              display: 'inline-flex',
              alignItems: 'center',
              borderRadius: 'var(--radius-pill)',
              fontSize: 'var(--type-caption-size)',
              fontWeight: 600,
              background: completed ? 'color-mix(in srgb, var(--hue-primary-vivid) 14%, transparent)' : 'var(--surface-hover)',
              color: completed ? 'var(--hue-primary)' : 'var(--text-secondary)',
            }}
          >
            {completed ? 'Done' : 'In progress'}
          </span>
          {!completed && available && (
            <span
              className="num"
              style={{ fontSize: 'var(--type-label-size)', letterSpacing: 'var(--type-label-tracking)', color: 'var(--text-secondary)' }}
            >
              +{reviewCompletedXp} XP when you finish
            </span>
          )}
        </div>
      </div>
    </header>
  );
}


/**
 * Sticky "at a glance" sidebar (Today/Objective-Form desktop-consistency
 * pass) — gives the widened 1600px shell a reason to exist beyond just
 * stretching the panel column. Purely presentational over values the shell
 * already computes; does NOT duplicate `FinishBar`'s action (no second
 * "Finish review" button) to avoid two controls doing the same thing —
 * this panel is glance-only.
 */
function ThisWeekSummary({
  weekStart,
  available,
  completed,
  pendingCount,
  reviewCompletedXp,
  habitCount,
  objectiveCount,
}: {
  readonly weekStart: WeekStart;
  readonly available: boolean;
  readonly completed: boolean;
  readonly pendingCount: number;
  readonly reviewCompletedXp: number;
  readonly habitCount: number;
  readonly objectiveCount: number;
}) {
  return (
    <aside
      aria-label="This week"
      style={{
        position: 'sticky',
        top: 'var(--space-8)',
        background: 'var(--surface-raised)',
        border: '1px solid var(--border-subtle)',
        borderRadius: 'var(--radius-xl)',
        padding: 'var(--layout-card-padding-desktop)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-4)',
      }}
    >
      <div>
        <span
          style={{
            fontSize: 'var(--type-label-size)',
            fontWeight: 'var(--type-label-weight)',
            letterSpacing: 'var(--type-label-tracking)',
            textTransform: 'uppercase',
            color: 'var(--text-muted)',
          }}
        >
          This week
        </span>
        <p style={{ margin: 'var(--space-1) 0 0', fontSize: 'var(--type-body-size)', fontWeight: 600, color: 'var(--text-primary)' }}>
          {formatWeekRange(weekStart)}
        </p>
      </div>

      {available && (
        <p style={{ margin: 0, fontSize: 'var(--type-body-size)', color: 'var(--text-secondary)' }}>
          {completed
            ? 'Review done for this week.'
            : pendingCount > 0
              ? `${pendingCount} question${pendingCount === 1 ? '' : 's'} left before you can finish.`
              : "Everything's answered — ready to finish."}
        </p>
      )}

      {available && !completed && (
        <span
          className="num"
          style={{ fontSize: 'var(--type-label-size)', letterSpacing: 'var(--type-label-tracking)', color: 'var(--hue-primary)', fontWeight: 600 }}
        >
          +{reviewCompletedXp} XP when you finish
        </span>
      )}

      <div style={{ display: 'flex', gap: 'var(--space-5)', paddingTop: 'var(--space-3)', borderTop: '1px solid var(--border-subtle)' }}>
        <SummaryStat value={habitCount} label="habits tracked" />
        <SummaryStat value={objectiveCount} label="objectives tracked" />
      </div>
    </aside>
  );
}

function SummaryStat({ value, label }: { readonly value: number; readonly label: string }) {
  return (
    <div>
      <div className="num" style={{ fontSize: 'var(--type-heading-size)', fontWeight: 700, color: 'var(--text-primary)', lineHeight: 1 }}>
        {value}
      </div>
      <div style={{ marginTop: 'var(--space-1)', fontSize: 'var(--type-caption-size)', color: 'var(--text-secondary)' }}>{label}</div>
    </div>
  );
}

function EmptyReview({ onBack }: { readonly onBack: () => void }) {
  return (
    <div style={{ marginTop: 'var(--space-8)', textAlign: 'center' }}>
      <p style={{ fontSize: 'var(--type-body-size)', color: 'var(--text-secondary)' }}>
        Nothing to review yet — set up a habit or an objective first.
      </p>
      <button
        type="button"
        onClick={onBack}
        style={{
          marginTop: 'var(--space-4)',
          minHeight: 'var(--touch-target-min)',
          padding: '0 var(--space-5)',
          borderRadius: 'var(--radius-md)',
          background: 'var(--hue-primary-vivid)',
          color: 'var(--text-on-accent)',
          fontWeight: 600,
          fontSize: 'var(--type-body-size)',
          border: '1px solid var(--hue-primary-strong)',
        }}
      >
        Go to Today
      </button>
    </div>
  );
}

function ReviewBadgeEarnLoader({ badgeId, onDismiss }: { readonly badgeId: string; readonly onDismiss: () => void }) {
  const api = useDomainApi();
  const [badge, setBadge] = useState<Awaited<ReturnType<typeof api.listBadges>>[number] | null>(null);

  useEffect(() => {
    let cancelled = false;
    void api.listBadges().then((badges) => {
      if (cancelled) return;
      setBadge(badges.find((b) => b.badgeId === badgeId) ?? null);
    });
    return () => {
      cancelled = true;
    };
  }, [api, badgeId]);

  if (!badge) return null;
  return <BadgeEarnSheet badge={badge} onDismiss={onDismiss} />;
}

// Re-exported for the objectives panel's checkpoint-answer typing convenience.
export type { ObjectiveReviewItem };
